// ─── Enums (mirrored from Prisma for frontend use) ─────────

export enum Role {
  ADMIN = 'ADMIN',
  MANAGER = 'MANAGER',
  SUPERVISOR = 'SUPERVISOR',
  EMPLOYEE = 'EMPLOYEE',
}

export enum UserStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ON_LEAVE = 'ON_LEAVE',
  SUSPENDED = 'SUSPENDED',
}

export enum AircraftCategory {
  REGIONAL = 'REGIONAL',
  MEDIUM = 'MEDIUM',
  HEAVY = 'HEAVY',
}

export enum FlightStatus {
  SCHEDULED = 'SCHEDULED',
  PREPARING = 'PREPARING',
  BOARDING = 'BOARDING',
  DEPARTED = 'DEPARTED',
  EN_ROUTE = 'EN_ROUTE',
  ARRIVED = 'ARRIVED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  DELAYED = 'DELAYED',
  DIVERTED = 'DIVERTED',
}

export enum FlightDirection {
  DEPARTURE = 'DEPARTURE',
  ARRIVAL = 'ARRIVAL',
}

export enum FlightPhase {
  PREPARATION = 'PREPARATION',
  TURNAROUND = 'TURNAROUND',
  ARRIVAL = 'ARRIVAL',
}

export enum TaskStatus {
  PENDING = 'PENDING',
  ASSIGNED = 'ASSIGNED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  BLOCKED = 'BLOCKED',
  SKIPPED = 'SKIPPED',
}

export enum MaterialStatus {
  AVAILABLE = 'AVAILABLE',
  IN_USE = 'IN_USE',
  OUT_OF_SERVICE = 'OUT_OF_SERVICE',
  MAINTENANCE = 'MAINTENANCE',
}

export enum ResourceType {
  HUMAN = 'HUMAN',
  MATERIAL = 'MATERIAL',
}

export enum ShiftStatus {
  SCHEDULED = 'SCHEDULED',
  CHECKED_IN = 'CHECKED_IN',
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  ABSENT = 'ABSENT',
  SICK = 'SICK',
}

export enum AircraftStatus {
  ACTIVE = 'ACTIVE',
  MAINTENANCE = 'MAINTENANCE',
  RETIRED = 'RETIRED',
}

export enum CostCategory {
  LABOR = 'LABOR',
  MATERIAL = 'MATERIAL',
  OVERTIME = 'OVERTIME',
  INCIDENT = 'INCIDENT',
  PENALTY = 'PENALTY',
  OTHER = 'OTHER',
}

export enum TaskStartTrigger {
  PHASE_START = 'PHASE_START',
  PHASE_END = 'PHASE_END',
  AFTER_TASK = 'AFTER_TASK',
}

export enum AssignmentStatus {
  ASSIGNED = 'ASSIGNED',
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  REASSIGNED = 'REASSIGNED',
}

export enum NotificationType {
  INCIDENT = 'INCIDENT',
  SICKNESS = 'SICKNESS',
  TOOL_FAILURE = 'TOOL_FAILURE',
  FLIGHT_DELAY = 'FLIGHT_DELAY',
  TASK_CHANGE = 'TASK_CHANGE',
  SCHEDULE_UPDATE = 'SCHEDULE_UPDATE',
  SYSTEM = 'SYSTEM',
  SHIFT_REMINDER = 'SHIFT_REMINDER',
}

// ─── API Response Types ─────────────────────────────────────

export interface ApiResponse<T> {
  data: T;
  meta?: {
    total?: number;
    page?: number;
    limit?: number;
  };
}

export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

// ─── Auth Types ─────────────────────────────────────────────

export interface AuthToken {
  token: string;
  expiresAt: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: Role;
    organizationId: string;
    baseId: string;
  };
}

// ─── Dashboard Types ────────────────────────────────────────

export interface DashboardData {
  flightsTotal: number;
  flightsActive: number;
  flightsDelayed: number;
  flightsCompleted: number;
  employeesOnShift: number;
  employeesAvailable: number;
  materialsInUse: number;
  materialsAvailable: number;
  materialsOutOfService: number;
  materialsMaintenance: number;
  tasksTotal: number;
  tasksCompleted: number;
  tasksPending: number;
  tasksInProgress: number;
  tasksBlocked: number;
  connectedUsers: number;
  alerts: DashboardAlert[];
}

export type AlertType = 'FLIGHT_DELAY' | 'RESOURCE_SHORTAGE' | 'TASK_OVERDUE' | 'SHIFT_GAP';

export interface DashboardAlert {
  id: string;
  type: AlertType;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  title: string;
  message: string;
  entityId?: string;
  createdAt: string;
}

// ─── Scheduler Types ────────────────────────────────────────

export interface ScheduleStats {
  totalFlights: number;
  totalTasks: number;
  assignedTasks: number;
  unassignedTasks: number;
  employeesUsed: number;
  materialsUsed: number;
  avgEmployeeUtilization: number;
  totalCost: number;
  executionTimeMs: number;
}

// ─── WebSocket Events ───────────────────────────────────────

export interface WsEvents {
  'flight:updated': { flightId: string; data: unknown };
  'flight:statusChanged': { flightId: string; status: FlightStatus };
  'task:updated': { taskId: string; data: unknown };
  'resource:updated': { resourceId: string; type: ResourceType; status: string };
  'notification:new': { notification: unknown };
  'schedule:updated': { baseId: string; date: string; stats: ScheduleStats };
}
